import React, { useEffect } from 'react';
import { Form, Formik } from 'formik';
import moment from 'moment';

import { toApiDate } from 'lib/dates';
import {
  BlockLoading,
  IMultiStepActions,
  MultiStepValidationError,
} from '@farmshare/ui-components';
import {
  AnimalHeadInput,
  CreateProcessorSchedulingMutation,
  CreateProcessorSchedulingMutationVariables,
  CustomFieldValueBulkSaveMutation,
  CustomFieldValueBulkSaveMutationVariables,
  EnumCustomFieldValueRecordType,
  EnumProcessorSchedulingAnimalHeadsInspectionLevel,
  EnumProcessorSchedulingAnimalSpecies,
  RecurringSeriesCreateFromJobMutation,
  RecurringSeriesCreateFromJobMutationVariables,
} from '../../../lib/graphql';
import {
  buildRecurrenceRule,
  parseKeepScheduled,
  recurrenceInitialValues,
} from 'features/jobs/recurring/recurrence';
import {
  mergeFlatErrorsIntoFormikErrors,
  serializeCustomFieldInput,
} from 'features/custom-fields';
import { useSettings } from 'providers';
import { userFacingError } from 'lib/user-facing-error';
import {
  DEPOSIT_METHOD,
  DepositCollectionSection,
} from 'features/jobs/components/deposit-collection-section';
import {
  isDepositApplicable,
  speciesDeposit,
} from 'features/jobs/helpers/species-deposit';
import type { MutationFunction } from '@apollo/client';
import type { IToastrContext } from '@farmshare/ui-components';
import {
  getSplitSlotsForType,
  isAnimalTouched,
  isMissing,
  ProducerSelectionStep,
  QuickScheduleForm,
  ReviewStep,
  ScheduleDetailsStep,
} from './quick-schedule';
import { type BookedJob, jobBookedToast } from '../lib/job-booked-toast';

export { ProducerSelectionStep, ScheduleDetailsStep, ReviewStep };

/**
 * Persist booking-time custom field values entered in the Review step.
 * On failure (job already created, bulk-save call rejected), surfaces a
 * non-fatal "Partial save" warning so the user can retry from the job
 * details page rather than silently losing entered values.
 * @param root0
 * @param root0.formValues
 * @param root0.jobId
 * @param root0.headIds
 * @param root0.bulkSaveCustomFieldValues
 * @param root0.pushToast
 */
export async function persistBookingCustomFieldValues({
  formValues,
  jobId,
  headIds,
  bulkSaveCustomFieldValues,
  pushToast,
}: {
  formValues: QuickScheduleForm;
  jobId: string;
  headIds: Array<string | undefined>;
  bulkSaveCustomFieldValues: MutationFunction<
    CustomFieldValueBulkSaveMutation,
    CustomFieldValueBulkSaveMutationVariables
  >;
  pushToast: IToastrContext['pushToast'];
}) {
  const fieldTypes = formValues._customFieldTypes ?? {};
  const inputs: NonNullable<ReturnType<typeof serializeCustomFieldInput>>[] =
    [];

  // Job-level values
  for (const [defId, val] of Object.entries(
    formValues.customFieldValues ?? {},
  )) {
    const input = serializeCustomFieldInput(
      { _id: defId, fieldType: fieldTypes[defId] },
      jobId,
      EnumCustomFieldValueRecordType.Job,
      val,
    );
    if (input) inputs.push(input);
  }

  // Per-head values
  formValues.head?.forEach((perHead, index) => {
    const headId = headIds[index];
    if (!headId || !perHead?.customFieldValues) return;
    for (const [defId, val] of Object.entries(perHead.customFieldValues)) {
      const input = serializeCustomFieldInput(
        { _id: defId, fieldType: fieldTypes[defId] },
        headId,
        EnumCustomFieldValueRecordType.Head,
        val,
      );
      if (input) inputs.push(input);
    }
  });

  if (inputs.length === 0) return;
  try {
    await bulkSaveCustomFieldValues({ variables: { inputs } });
  } catch (e) {
    // Schedule already created; surface partial-save so the user can retry
    // from the job details page instead of silently losing values.
    // eslint-disable-next-line no-console
    console.error('Failed to save booking custom field values', e);
    pushToast({
      title: 'Job booked, but not every field saved',
      body: 'Some custom fields didn’t save. Open the job to fill them in again.',
      severity: 'warning',
    });
  }
}

// Wrapper component for Details & Date step
const DetailsStepWrapper = ({
  data,
  actions,
  vendorId,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
  vendorId: string;
}) => {
  const { settings } = useSettings();
  const hasSetSettingsId = React.useRef(false);

  const processorSettings = settings?.data?.processorSettingsOne;

  useEffect(() => {
    // Update form data with processorSettingsId only once when available
    if (processorSettings && !hasSetSettingsId.current) {
      hasSetSettingsId.current = true;
      actions.updateData({
        ...data,
        processorSettingsId: processorSettings._id,
      });
    }
  }, [processorSettings, data, actions]);

  if (settings?.loading || !processorSettings) {
    return <BlockLoading />;
  }

  return (
    <Formik
      initialValues={data}
      validate={(values: QuickScheduleForm) => {
        const errors: Record<string, string> = {};
        if (!values?.species) errors.species = 'Species is required';
        if (!values?.quantity || values.quantity < 1)
          errors.quantity = 'Quantity must be at least 1';
        if (!values?.date) errors.date = 'Date is required';
        return errors;
      }}
      // Enter-key submit only ADVANCES the wizard (validation gates it). The
      // create runs once at completion via the guarded renderer `next()` ->
      // `onComplete`, never from a step form — so a stray form submit can't
      // create a duplicate job/animal set.
      onSubmit={() => actions.next()}
    >
      {({ values }) => {
        // Update multiStep data when form values change
        // Use ref to prevent infinite loops from object reference changes
        const valuesRef = React.useRef<QuickScheduleForm | null>(null);
        React.useEffect(() => {
          // Recompute whether the final Deposit step applies for the chosen
          // species so it shows/hides live as the user edits this step.
          const merged: QuickScheduleForm = {
            ...values,
            depositApplicable: isDepositApplicable(
              processorSettings,
              values.species,
            ),
          };
          if (JSON.stringify(valuesRef.current) !== JSON.stringify(merged)) {
            valuesRef.current = merged;
            actions.updateData(merged);
          }
        }, [values]);

        return (
          <Form>
            <ScheduleDetailsStep
              vendorId={vendorId}
              processorSettings={processorSettings}
              onNext={actions.next}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

/**
 * Lets the Review step register a validate-and-touch callback against the
 * outer multi-step's `onNext`. The pattern across the app is to keep the
 * action button enabled and surface inline errors on click — so we can't gate
 * via `canGoNext`, and the multi-step's next button doesn't run Formik submit.
 * The step's `onNext` reads from this ref to run validation just before
 * `onComplete` fires.
 */
type ReviewValidator = () => Promise<boolean>;

function hasErrorUnderKey(
  errors: Record<string, unknown>,
  key: string,
): boolean {
  const val = errors[key];
  if (!val) return false;
  if (Array.isArray(val)) return val.some((entry) => entry != null);
  if (typeof val === 'object') return Object.keys(val).length > 0;
  return true;
}

/**
 * Mirror a Formik errors tree into a touched tree of the same shape, with
 * `true` at each leaf. Arrays must be preserved as arrays — `setTouched`
 * matches structure to field paths, and collapsing `head: [{...}]` into
 * `head: true` would prevent the per-animal inputs from rendering their
 * inline errors.
 * @param errors
 */
function buildTouchedFromErrors(errors: unknown): unknown {
  if (errors === null || errors === undefined) return undefined;
  if (Array.isArray(errors)) {
    return errors.map((entry) => buildTouchedFromErrors(entry));
  }
  if (typeof errors === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(errors)) {
      const touched = buildTouchedFromErrors(val);
      if (touched !== undefined) out[key] = touched;
    }
    return out;
  }
  return true;
}

/**
 * Generic per-required-field validator for the Review step. We don't have the
 * full definitions in this Formik scope (ReviewStep loads them), but the form
 * stashes `_requiredCustomFieldIds` and the inputs render a `Required` label;
 * a generic "Required" error message at the matching dot-path is enough to
 * surface the missing field and gate submission via Formik.
 * @param values
 */
function validateReviewStepCustomFields(
  values: QuickScheduleForm,
): Record<string, unknown> | undefined {
  const required = values._requiredCustomFieldIds;
  if (!required) return undefined;
  const flat: Record<string, string> = {};
  for (const id of required.job ?? []) {
    if (isMissing(values.customFieldValues?.[id])) {
      flat[`customFieldValues.${id}`] = 'Required';
    }
  }
  // Per-animal head fields live inside the "Animal Details (Optional)"
  // accordion, which most users never open. Only enforce required head
  // fields for animals the user has actually entered data about — once
  // they engage with that animal, every required field for it must be
  // filled.
  for (let i = 0; i < (values.quantity ?? 0); i++) {
    if (!isAnimalTouched(values, i)) continue;
    for (const id of required.head ?? []) {
      const headCfv = values.head?.[i]?.customFieldValues?.[id];
      if (isMissing(headCfv)) {
        flat[`head.${i}.customFieldValues.${id}`] = 'Required';
      }
    }
  }
  if (Object.keys(flat).length === 0) return undefined;
  return mergeFlatErrorsIntoFormikErrors(flat);
}

// Wrapper component for Review step
const ReviewStepWrapper = ({
  data,
  actions,
  vendorId,
  validatorRef,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
  vendorId: string;
  validatorRef: { current: ReviewValidator | null };
}) => {
  const { settings } = useSettings();
  const hasSetSettingsId = React.useRef(false);

  const processorSettings = settings?.data?.processorSettingsOne;

  useEffect(() => {
    // Update form data with processorSettingsId only once when available
    if (processorSettings && !hasSetSettingsId.current) {
      hasSetSettingsId.current = true;
      actions.updateData({
        ...data,
        processorSettingsId: processorSettings._id,
      });
    }
  }, [processorSettings, data, actions]);

  if (settings?.loading || !processorSettings) {
    return <BlockLoading />;
  }

  return (
    <Formik
      initialValues={data}
      enableReinitialize
      validate={validateReviewStepCustomFields}
      // Last step: an Enter-key submit routes through the guarded renderer
      // `next()` -> `onNext` (runs the review validator) -> `onComplete` (calls
      // `handleSubmit` once), the same single create path as the footer
      // "Schedule Job" button. The renderer's in-flight ref de-dupes
      // Enter-then-click.
      onSubmit={() => actions.next()}
    >
      {(formik) => {
        const { values, validateForm, setTouched } = formik;
        const expandHeadDetailsRef = React.useRef<(() => void) | null>(null);
        // Update multiStep data when form values change
        // Use ref to prevent infinite loops from object reference changes
        const valuesRef = React.useRef(values);
        React.useEffect(() => {
          if (JSON.stringify(valuesRef.current) !== JSON.stringify(values)) {
            valuesRef.current = values;
            actions.updateData(values);
          }
        }, [values]);

        // Expose a validate-and-touch handler so the multi-step's `onNext`
        // can mark required custom field paths touched (rendering inline
        // errors) and short-circuit before `onComplete` runs.
        React.useEffect(() => {
          // Local alias: `validatorRef` is a ref object handed down by the
          // caller so this effect can register a validator against it — the
          // caller reads the same ref afterwards, so mutating a fresh object
          // would be dropped (no-param-reassign).
          const ref = validatorRef;
          ref.current = async () => {
            const errors = (await validateForm()) as Record<string, unknown>;
            if (Object.keys(errors).length === 0) return true;
            const touched = buildTouchedFromErrors(errors);
            await setTouched(
              touched as Parameters<typeof setTouched>[0],
              false,
            );
            // If any per-animal field errored, force the accordion open so
            // the inline `Required` messages aren't trapped behind a closed
            // section.
            if (hasErrorUnderKey(errors, 'head')) {
              expandHeadDetailsRef.current?.();
            }
            return false;
          };
          return () => {
            ref.current = null;
          };
        }, [validateForm, setTouched]);

        return (
          <Form>
            <ReviewStep
              vendorId={vendorId}
              processorSettings={processorSettings}
              expandHeadDetailsRef={expandHeadDetailsRef}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

/**
 * Final "Deposit" step for the processor on-behalf flow: lets the processor
 * choose how to collect the deposit (don't collect / card on file / send
 * notification). The step is only rendered when it applies (gated by the
 * step's `visible` predicate on `data.depositApplicable`), so it appears and
 * disappears from the flow as the booked species changes. The choice is stored
 * on multi-step `data.depositCollection` so `canGoNext` reacts and
 * `handleSubmit` reads it.
 * @param root0
 * @param root0.data
 * @param root0.actions
 * @param root0.vendorId
 */
const DepositStepWrapper = ({
  data,
  actions,
  vendorId,
}: {
  data: QuickScheduleForm;
  actions: IMultiStepActions<QuickScheduleForm>;
  vendorId: string;
}) => {
  const { settings } = useSettings();
  const processorSettings = settings?.data?.processorSettingsOne;
  // Per-species deposit (per head) for the booked species.
  const schedulingDeposit = speciesDeposit(
    (processorSettings as { animalSettings?: unknown })
      ?.animalSettings as Parameters<typeof speciesDeposit>[0],
    data.species,
  );
  const connectedAccountId = (
    processorSettings as { vendor?: { stripe_config?: { id?: string } } }
  )?.vendor?.stripe_config?.id;

  if (settings?.loading || !processorSettings) return <BlockLoading />;

  return (
    <DepositCollectionSection
      vendorId={vendorId}
      producerId={data.requesterContactInformation?.producer ?? ''}
      connectedAccountId={connectedAccountId}
      headCount={data.quantity ?? 0}
      schedulingDeposit={schedulingDeposit}
      value={data.depositCollection ?? { method: DEPOSIT_METHOD.None }}
      onChange={(v) => actions.updateData({ depositCollection: v })}
    />
  );
};

// Export the multiStep configuration factory
export function createQuickScheduleMultiStepConfig({
  vendorId,
  initialValues,
  initialFormOverrides,
  waitlistId,
  onSuccess,
  createScheduling,
  bulkSaveCustomFieldValues,
  createRecurringSeries,
  pushToast,
  analyticsEntry,
}: {
  vendorId: string;
  /**
   * Which control opened the wizard (`calendar`, `jobs_page`, …), reported on
   * its funnel events. Several entry points share a page, so the page alone
   * can't tell them apart.
   */
  analyticsEntry?: string;
  initialValues?: {
    initialDate?: string;
    initialProducer?: string;
  };
  /** Optional pre-filled form values (species/requesterContactInformation/etc.) — merged on top of defaults. */
  initialFormOverrides?: Partial<QuickScheduleForm>;
  /** Waitlist entry that this booking is fulfilling. Linked on the resulting job. */
  waitlistId?: string;
  onSuccess?: (scheduleId: string) => void;
  createScheduling: MutationFunction<
    CreateProcessorSchedulingMutation,
    CreateProcessorSchedulingMutationVariables
  >;
  /**
   * Persists booking-time custom field values entered in the Review step
   *  after the schedule is created. Required so every quick-schedule
   *  entrypoint wires persistence consistently.
   */
  bulkSaveCustomFieldValues: MutationFunction<
    CustomFieldValueBulkSaveMutation,
    CustomFieldValueBulkSaveMutationVariables
  >;
  /**
   * Turns the created job into a recurring series when the Review step's
   *  "Make this a recurring job" box is checked. Optional — entrypoints that
   *  don't wire it simply won't offer recurring scheduling.
   */
  createRecurringSeries?: MutationFunction<
    RecurringSeriesCreateFromJobMutation,
    RecurringSeriesCreateFromJobMutationVariables
  >;
  capacityData: unknown;
  pushToast: IToastrContext['pushToast'];
}) {
  /**
   * Books the job. Failures are shown here, as toasts, and reported by
   * resolving `false`: the wizard then stays open (the create is idempotent on
   * `requestId`, so Schedule Job can be pressed again) and the run is not
   * counted as a completed booking.
   * @returns Whether the job was created
   */
  const handleSubmit = async (
    formValues: QuickScheduleForm,
    processorSettingsId: string,
  ): Promise<boolean> => {
    if (!vendorId || !processorSettingsId) {
      pushToast({
        title: "Couldn't book the job",
        body: 'Your booking settings didn’t load. Refresh and try again.',
        bg: 'danger',
      });
      return false;
    }

    const animalHeads: AnimalHeadInput[] = Array.from(
      { length: formValues.quantity },
      (_, index) => {
        // Build cutsheet information from split assignments
        const cutsheetInformation: AnimalHeadInput['cutsheetInformation'] = [];
        if (formValues.splitTypes?.[index]) {
          const slots = getSplitSlotsForType(formValues.splitTypes[index]);
          slots.forEach((slot, slotIdx) => {
            // Check if buyer is selected (ID) or inline data provided
            const buyerId = formValues.splitAssignments?.[index]?.[slotIdx];
            const inlineBuyer = formValues.splitBuyers?.[index]?.[slotIdx];

            if (buyerId) {
              // Existing buyer selected
              cutsheetInformation.push({
                splitPart: slot.splitPart,
                contactUser: buyerId,
                quantity: 1,
              });
            } else if (inlineBuyer?.first_name) {
              // Inline buyer data - pass via contactInformation
              cutsheetInformation.push({
                splitPart: slot.splitPart,
                contactInformation: {
                  firstName: inlineBuyer.first_name,
                  lastName: inlineBuyer.last_name,
                  email: inlineBuyer.email,
                  phone: inlineBuyer.phone,
                  company: inlineBuyer.company,
                },
                quantity: 1,
              });
            }
          });
        }

        return {
          inspectionLevel: formValues.inspectionLevel,
          producerIdentifier: formValues.producerIdentifiers?.[index] || '',
          cutsheetInformation,
          ...(formValues.sexes?.[index] && { sex: formValues.sexes[index] }),
          ...(formValues.isOverThirtyMonths?.[index] !== undefined && {
            isOverThirtyMonths: formValues.isOverThirtyMonths[index],
          }),
          ...(formValues.isKillAndChill?.[index] !== undefined && {
            isKillAndChill: formValues.isKillAndChill[index],
          }),
          ...(formValues.splitTypes?.[index] && {
            splitType: formValues.splitTypes[index],
          }),
          ...(formValues.hangTimeDays?.[index] != null && {
            hangTimeDays: formValues.hangTimeDays[index],
          }),
        };
      },
    );

    // Transform requesterContactInformation from snake_case to camelCase for GraphQL
    const requesterContactInformation = {
      producer: formValues.requesterContactInformation.producer,
      firstName: formValues.requesterContactInformation.first_name,
      lastName: formValues.requesterContactInformation.last_name,
      email: formValues.requesterContactInformation.email,
      phone: formValues.requesterContactInformation.phone,
      company: formValues.requesterContactInformation.company,
    };

    const dropoffDate = toApiDate(formValues.date);
    if (!dropoffDate) {
      pushToast({
        title: 'Pick a drop-off date',
        body: 'Choose a valid drop-off date to book the job.',
        bg: 'danger',
      });
      return false;
    }
    try {
      const result = await createScheduling({
        // Refresh the calendar by re-running its Apollo query by operation name
        // (SUP-246): FullCalendar's refetchEvents() is a no-op for our
        // array-backed event source, so a newly scheduled job must come from
        // the query. Name-based refetch updates whichever
        // ProcessorSchedulingCalendar observer is mounted (desktop or mobile)
        // and no-ops elsewhere. Deliberately NOT awaited: awaitRefetchQueries
        // would reject this mutation if a refetch failed — failing an
        // already-created job — and the calendar updates reactively anyway
        // once the refetch lands.
        refetchQueries: ['ProcessorSchedulingCalendar'],
        variables: {
          newSchedule: {
            requestId: formValues.requestId,
            animalSpecies: formValues.species,
            dropoffDate,
            headCount: formValues.quantity,
            processorSettings: processorSettingsId,
            vendor: vendorId,
            animalHeads,
            requesterContactInformation,
            refundDeadlineDays: 0,
            isOnBehalfOf: true,
            endDate: toApiDate(moment(formValues.date).add(6, 'days')),
            blockIndex: formValues.blockIndex,
            blockTitle: formValues.blockTitle,
            // Dropped server-side for a vendor without the JOB_TAGS module;
            // the booking still goes through.
            tags: formValues.tags ?? [],
            waitlist: waitlistId,
            // Recurring jobs never collect a deposit — only the seed job would
            // be charged, leaving occurrences deposit-less (PRO-586) — so a
            // recurring job is sent as "Don't collect", whatever was picked.
            // "Don't collect" (also the picker's untouched default) is sent,
            // not left out, so the job records it and shows a "Didn't
            // collect" deposit card (SUP-464). The server ignores it for a
            // species that takes no deposit.
            depositCollection:
              !formValues.makeRecurring && formValues.depositCollection
                ? formValues.depositCollection
                : { method: DEPOSIT_METHOD.None },
          },
        },
      });

      const created = (
        result as {
          data?: {
            processorSchedulingCreate?: {
              scheduling?: BookedJob & {
                _id?: string;
                animalHeads?: Array<{ _id?: string } | null> | null;
              };
              bookingFollowUp?: BookedJob['bookingFollowUp'];
            };
          };
        }
      )?.data?.processorSchedulingCreate;
      const scheduling = created?.scheduling;
      const newScheduleId = scheduling?._id;

      if (newScheduleId) {
        await persistBookingCustomFieldValues({
          formValues,
          jobId: newScheduleId,
          headIds:
            scheduling?.animalHeads?.map((h) => h?._id ?? undefined) ?? [],
          bulkSaveCustomFieldValues,
          pushToast,
        });

        // If the user checked "Make this a recurring job" on the Review step,
        // turn the just-created job into a recurring series. Non-fatal: the job
        // is already booked, so a failure here only warns.
        if (
          formValues.makeRecurring &&
          formValues.recurrence &&
          createRecurringSeries
        ) {
          try {
            const rule = buildRecurrenceRule(
              formValues.recurrence,
              formValues.date,
            );
            await createRecurringSeries({
              variables: {
                processorSchedulingId: newScheduleId,
                rule,
                targetUpcomingCount: parseKeepScheduled(
                  formValues.recurrence.targetUpcomingCount,
                ),
              },
            });
          } catch (recurringError) {
            // eslint-disable-next-line no-console
            console.error('Failed to create recurring series', recurringError);
            pushToast({
              title: 'Job booked, but it isn’t recurring yet',
              body: userFacingError(
                recurringError,
                'You can make it recurring from the job’s details page.',
              ),
              severity: 'warning',
            });
          }
        }

        pushToast({
          ...jobBookedToast(
            { ...scheduling, bookingFollowUp: created?.bookingFollowUp },
            'processor',
          ),
          severity: 'success',
          delay: 8000,
          showConfetti: true,
          action: {
            label: 'View Job',
            href: `/processing-job/${newScheduleId}/cutsheets`,
          },
        });
        onSuccess?.(newScheduleId);
        // Don't call onClose here - the multiStep modal handles it after onComplete
        return true;
      }
      pushToast({
        title: "Couldn't book the job",
        body: 'Something went wrong. Try again.',
        severity: 'error',
      });
      return false;
    } catch (error) {
      pushToast({
        title: "Couldn't book the job",
        body: userFacingError(error, 'Something went wrong. Try again.'),
        severity: 'error',
      });
      return false;
    }
  };

  // Owned by the multistep config so ReviewStepWrapper can register a
  // validate-and-touch callback, and the Review step's `onNext` can run it
  // before the multistep calls `onComplete`.
  const reviewValidatorRef: { current: ReviewValidator | null } = {
    current: null,
  };

  const initialFormValues: QuickScheduleForm = {
    // Idempotency token — stable per wizard mount (the renderer snapshots
    // initialData once). Sent on the create so a double-submit / retry returns
    // the existing job instead of minting a duplicate.
    requestId: crypto.randomUUID(),
    requesterContactInformation: {
      producer: initialValues?.initialProducer || '',
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company: '',
    },
    species: EnumProcessorSchedulingAnimalSpecies.Beef,
    inspectionLevel: EnumProcessorSchedulingAnimalHeadsInspectionLevel.Exempt,
    quantity: 1,
    date:
      initialValues?.initialDate || moment().add(1, 'day').format('YYYY-MM-DD'),
    blockTitle: '',
    blockIndex: undefined,
    producerIdentifiers: [],
    sexes: [],
    isOverThirtyMonths: [],
    isKillAndChill: [],
    splitTypes: [],
    splitAssignments: {},
    makeRecurring: false,
    recurrence: { ...recurrenceInitialValues, targetUpcomingCount: '4' },
    ...initialFormOverrides,
  };

  return {
    analyticsName: 'quick_schedule',
    analyticsContext: { entry: analyticsEntry },
    // No title: the step chevrons ("Details & Date" → "Review") already say what
    // this is, and dropping the header buys back a row of vertical space that
    // the dense steps need on a phone. The renderer promotes the stepper to a
    // flush, square-cornered header when there's no title.
    size: 'sm' as const,
    // The steps are dense — date picker, producer search, split assignments,
    // custom fields, deposit — and a centered `sm` dialog leaves them scrolling
    // inside a box inside a scrolling page on a phone. Go full-bleed there, the
    // same treatment the calendar's Schedule Job side-over gets on mobile (see
    // pages/processor/calendar/_views/schedule-job.tsx). Desktop is unchanged.
    fullScreenOnMobile: true,
    // With no header there's no close X, so the footer carries the exit. Only
    // needed on step one — every later step has Back to walk out through.
    showCancelOnFirstStep: true,
    initialData: initialFormValues,
    onComplete: async (data: QuickScheduleForm) => {
      // Get processorSettingsId from form data (set by wrapper components)
      const { processorSettingsId } = data;

      if (!processorSettingsId) {
        pushToast({
          title: "Couldn't book the job",
          body: 'Your processor settings didn’t load. Refresh and try again.',
          severity: 'error',
        });
        return false;
      }

      return handleSubmit(data, processorSettingsId);
    },
    steps: [
      {
        title: 'Details & Date',
        analyticsKey: 'details_date',
        body: (
          data: QuickScheduleForm,
          actions: IMultiStepActions<QuickScheduleForm>,
        ) => (
          <DetailsStepWrapper
            data={data}
            actions={actions}
            vendorId={vendorId}
          />
        ),
        canGoNext: (data: QuickScheduleForm) =>
          Boolean(
            data?.quantity &&
              data?.species &&
              data?.inspectionLevel &&
              data?.date &&
              data?.blockTitle,
          ),
      },
      {
        title: 'Select Producer',
        analyticsKey: 'select_producer',
        body: (
          data: QuickScheduleForm,
          actions: IMultiStepActions<QuickScheduleForm>,
        ) => (
          <Formik
            initialValues={data}
            enableReinitialize
            validate={(values: QuickScheduleForm) => {
              const errors: Record<string, string> = {};
              const hasProducer = Boolean(
                values?.requesterContactInformation?.producer,
              );
              const hasFirstName = Boolean(
                values?.requesterContactInformation?.first_name,
              );
              const hasEmail = Boolean(
                values?.requesterContactInformation?.email,
              );
              const hasPhone = Boolean(
                values?.requesterContactInformation?.phone,
              );
              const hasContactMethod = hasEmail || hasPhone;
              const hasManualInfo = hasFirstName && hasContactMethod;

              // Validation debug logging removed

              if (!hasProducer && !hasManualInfo) {
                errors.requesterContactInformation = 'Producer is required';
              }
              return errors;
            }}
            onSubmit={(values) => {
              // Just update the data, don't submit yet
              actions.updateData(values);
            }}
          >
            {({ values }) => {
              // Update multiStep data when form values change
              // Use ref to prevent infinite loops from object reference changes
              const valuesRef = React.useRef(values);
              React.useEffect(() => {
                if (
                  JSON.stringify(valuesRef.current) !== JSON.stringify(values)
                ) {
                  valuesRef.current = values;
                  actions.updateData(values);
                }
              }, [values]);

              return (
                <Form>
                  <ProducerSelectionStep
                    vendorId={vendorId}
                    onNext={actions.next}
                  />
                </Form>
              );
            }}
          </Formik>
        ),
        canGoNext: (data: QuickScheduleForm) => {
          const hasProducer = Boolean(
            data?.requesterContactInformation?.producer,
          );
          const hasFirstName = Boolean(
            data?.requesterContactInformation?.first_name,
          );
          const hasEmail = Boolean(data?.requesterContactInformation?.email);
          const hasPhone = Boolean(data?.requesterContactInformation?.phone);
          const hasContactMethod = hasEmail || hasPhone;
          const hasManualInfo = hasFirstName && hasContactMethod;
          const canProceed = hasProducer || hasManualInfo;

          // Validation check logging removed

          return canProceed;
        },
      },
      {
        title: 'Review',
        analyticsKey: 'review',
        body: (
          data: QuickScheduleForm,
          actions: IMultiStepActions<QuickScheduleForm>,
        ) => (
          <ReviewStepWrapper
            data={data}
            actions={actions}
            vendorId={vendorId}
            validatorRef={reviewValidatorRef}
          />
        ),
        // When no deposit applies — or the job is recurring, which never
        // collects a deposit (PRO-586) — Review is the final step, so submit
        // from here.
        nextButtonText: (data: QuickScheduleForm) =>
          data.depositApplicable && !data.makeRecurring
            ? 'Next'
            : 'Schedule Job',
        onNext: async () => {
          const validate = reviewValidatorRef.current;
          if (!validate) return;
          const ok = await validate();
          if (!ok) {
            // Throw to short-circuit the multistep's `onComplete` while letting
            // the renderer's catch swallow the error silently. Inline field
            // errors are now visible to the user. A validation error, so the
            // funnel doesn't count it as a failure.
            throw new MultiStepValidationError('REVIEW_VALIDATION_FAILED');
          }
        },
        canGoNext: (data: QuickScheduleForm) => {
          // Check if requester contact info is complete
          const hasProducer = Boolean(
            data?.requesterContactInformation?.producer,
          );
          const hasFirstName = Boolean(
            data?.requesterContactInformation?.first_name,
          );
          const hasEmail = Boolean(data?.requesterContactInformation?.email);
          const hasPhone = Boolean(data?.requesterContactInformation?.phone);
          const hasContactMethod = hasEmail || hasPhone;
          const hasProducerInfo =
            hasProducer || (hasFirstName && hasContactMethod);

          if (!hasProducerInfo) {
            // Validation failed: Missing producer info
            return false;
          }

          // Note: required booking-time custom fields are NOT gated here —
          // the convention is to leave the action button enabled and surface
          // inline errors on click. The step's `onNext` (see below) runs the
          // Review Formik's validate and aborts submission when required
          // fields are missing, while marking those fields touched so their
          // inline error messages render.

          // Check split assignments if split types are defined
          if (data?.splitTypes && data.splitTypes.length > 0) {
            for (let i = 0; i < data.quantity; i++) {
              if (data.splitTypes[i]) {
                const slots = getSplitSlotsForType(data.splitTypes[i]);
                for (let slotIdx = 0; slotIdx < slots.length; slotIdx++) {
                  const hasBuyerId = Boolean(
                    data.splitAssignments?.[i]?.[slotIdx],
                  );
                  const inlineBuyer = data.splitBuyers?.[i]?.[slotIdx];

                  // Only fail if there's an inline buyer with incomplete information
                  if (!hasBuyerId && inlineBuyer) {
                    const isInlineBuyerComplete =
                      Boolean(inlineBuyer.first_name) &&
                      (Boolean(inlineBuyer.email) ||
                        Boolean(inlineBuyer.phone));

                    if (!isInlineBuyerComplete) {
                      // Validation failed: Incomplete inline buyer data
                      return false;
                    }
                  }
                }
              }
            }
          }

          return true;
        },
      },
      {
        title: 'Deposit',
        analyticsKey: 'deposit',
        // Shown only when the booked species takes a deposit and the processor
        // can collect one; toggles live as the species changes in Details.
        // Never shown for recurring jobs — occurrences can't collect a deposit,
        // so deposits and recurrence are mutually exclusive (PRO-586).
        visible: (data: QuickScheduleForm) =>
          Boolean(data.depositApplicable) && !data.makeRecurring,
        body: (
          data: QuickScheduleForm,
          actions: IMultiStepActions<QuickScheduleForm>,
        ) => (
          <DepositStepWrapper
            data={data}
            actions={actions}
            vendorId={vendorId}
          />
        ),
        nextButtonText: 'Schedule Job',
        // Require a card when charging a card on file; other methods are fine.
        canGoNext: (data: QuickScheduleForm) =>
          data.depositCollection?.method !== DEPOSIT_METHOD.CardOnFile ||
          Boolean(data.depositCollection?.paymentMethodId),
      },
    ],
  };
}
